import { centerOfMass } from '@turf/turf';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  cn,
  Divider,
  RiCompass4Line,
  Stack,
} from 'component-library';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CellProperties } from '@/api/rest/resources/conservation';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { useIsRestorationProject } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/hooks/useIsRestorationProject';
import {
  FactCapsule,
  FactCapsuleTrend,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { Area, Dimension } from '@/utils/area';
import { isNullish } from '@/utils/booleans';

import { useControlsContext } from '../hooks/useControlsForm';

const UNIT_MAP: Partial<{
  [key in UnitEnum]: Dimension;
}> = {
  [UnitEnum.ha]: 'hectares',
  [UnitEnum['km^2']]: 'squareKilometers',
  [UnitEnum['m^2']]: 'squareMeters',
} as const;

export const InfoPanel = () => {
  const { t } = useTranslation();
  const isRestorationProject = useIsRestorationProject();

  const indicatorSpeciesTSFact = useFact('r1_conservation_indicator_species_ts');
  const ecologicalIntegrityTSFact = useFact('r1_conservation_ecological_integrity_ts');

  const controlsForm = useControlsContext();
  const cell = controlsForm.watch('cell');

  const areaUnits = useCellFact('area_units');
  let dimension: Dimension = isRestorationProject ? 'hectares' : 'squareKilometers';
  if (!isNullish(areaUnits.value)) {
    dimension = UNIT_MAP[areaUnits.value as keyof typeof UNIT_MAP] ?? dimension;
  }

  const areaDisplay = new Area({
    value: cell?.properties.area_cut ?? cell?.properties.area ?? 0,
    dimension,
  }).printDisplay();

  if (!cell) {
    return null;
  }

  const cellCoordinates = centerOfMass(cell).geometry.coordinates.map((c) => c.toFixed(6));
  const cellCoordinatesDisplay = `${cellCoordinates[0]} S, ${cellCoordinates[1]} E`;

  const closePanel = () => {
    controlsForm.setValue('cell', undefined);
  };

  const displayFaunaAndFloraDropdown = !isRestorationProject && !isNullish(indicatorSpeciesTSFact?.value); // Dashboards with species occupancy only do not have this metric at a cell level, only at project level (+ restoration dashboards are an exception)
  const displayEcologicalIntegrity = !isRestorationProject && !isNullish(ecologicalIntegrityTSFact?.value);

  return (
    <Stack className='h-full w-[413px] bg-white-100' data-testid='interactive-map-info-panel'>
      <Stack spacing={2} className='flex-1 overflow-auto pb-8'>
        <Stack spacing={4} className='mx-6 mt-8 rounded-xl bg-neutral-black-4 p-4'>
          <Stack direction='row' centerMain spacing={8}>
            <InfoStack
              label={t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.cellId')}
              content={cell.properties.id ?? cell.properties.name}
            />
            <InfoStack
              label={t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.unitSize')}
              content={areaDisplay}
            />
          </Stack>
          <InfoStack
            label={t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.cellCoordinates')}
            content={cellCoordinatesDisplay}
          />
        </Stack>

        <Accordion className='group/accordion flex flex-col px-4' type='single' collapsible>
          {displayEcologicalIntegrity && (
            <AccordionItem value='about'>
              <AccordionTrigger>
                {t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.ecologicalIntegrity')}
              </AccordionTrigger>
              <AccordionContent>
                <EcologicalIntegrity />
              </AccordionContent>
              <AccordionDivider />
            </AccordionItem>
          )}

          <AccordionItem value='species'>
            <AccordionTrigger>
              {/* eslint-disable-next-line sonarjs/no-duplicate-string */}
              {t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.habitatIntactness')}
            </AccordionTrigger>
            <AccordionContent>
              <HabitatIntactness />
            </AccordionContent>
            <AccordionDivider />
          </AccordionItem>

          {displayFaunaAndFloraDropdown && (
            <AccordionItem value='community'>
              <AccordionTrigger>
                {t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.floraAndFauna')}
              </AccordionTrigger>
              <AccordionContent className='pt-4'>
                <FloraAndFauna />
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </Stack>
      <Stack
        direction='row'
        className={cn('justify-end justify-self-end px-6 py-4', '[box-shadow:_0px_-10px_10px_0px_rgba(0,0,0,0.05)]')}
      >
        <Button variant='outline' onClick={closePanel}>
          {t('global.ui.buttons.close')}
        </Button>
      </Stack>
    </Stack>
  );
};

const InfoStack = ({ label, content }: { label: React.ReactNode; content: React.ReactNode }) => {
  return (
    <Stack spacing={2} className='min-w-0 flex-1'>
      <span
        className='typography-overline truncate text-text-secondary'
        title={typeof label === 'string' ? label : undefined}
      >
        {label}
      </span>
      <span className='typography-h4 truncate' title={typeof content === 'string' ? content : undefined}>
        {content}
      </span>
    </Stack>
  );
};

const AccordionDivider = () => <Divider className='mx-4' />;

const LAST_AVERAGE_VALUE_LABEL =
  'shared.projects.project.conservation.interactiveMap.infoPanel.labels.latestAverageValue';
const AVERAGE_VALUE_TITLE_LABEL =
  'shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.averageValue.title';
const AVERAGE_VALUE_DESCRIPTION_LABEL =
  'shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.averageValue.description';

const EcologicalIntegrity = () => {
  const { t } = useTranslation();

  const currentYear = useFact('r1_conservation_current_year').value;
  const defaultLatestAverage = useCellFact('ecological_integrity');
  const vnuLatestAverage = useCellFact('ecological_integrity_ts');
  const latestAverage = !isNullish(defaultLatestAverage.value) ? defaultLatestAverage : vnuLatestAverage;

  return (
    <Stack spacing={7}>
      <FactCapsule
        label={t(LAST_AVERAGE_VALUE_LABEL, {
          year: currentYear,
        })}
        fact={latestAverage}
        capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
        infoPopoverProps={{
          title: t(AVERAGE_VALUE_TITLE_LABEL, {
            year: currentYear,
          }),
          body: t(AVERAGE_VALUE_DESCRIPTION_LABEL, {
            year: currentYear,
            indicator: t(
              'shared.projects.project.conservation.interactiveMap.infoPanel.labels.ecologicalIntegrity',
            ).toLowerCase(),
          }),
        }}
      />
    </Stack>
  );
};

const HabitatIntactness = () => {
  const { t } = useTranslation();

  const currentYear = useFact<number>('r1_conservation_current_year').value ?? 2023;
  const baselineYear = useFact<number>('r1_conservation_baseline_year').value ?? 2018;

  const latestAverage = useCellFact('threats_i');

  const change = useCellFact('threats_i_change');

  const changePercentage = useCellFact('threats_i_change_percentage');

  // eslint-disable-next-line no-nested-ternary
  const { trendIcon, variant } = changePercentage?.value
    ? changePercentage?.value > 0
      ? { trendIcon: 'ri-arrow-right-up-line', variant: 'positive' }
      : { trendIcon: 'ri-arrow-right-down-line', variant: 'negative' }
    : { trendIcon: 'ri-compass4-line', variant: 'default' };

  return (
    <Stack spacing={7}>
      <FactCapsule
        label={t(LAST_AVERAGE_VALUE_LABEL, {
          year: currentYear,
        })}
        fact={latestAverage}
        capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
        infoPopoverProps={{
          title: t(AVERAGE_VALUE_TITLE_LABEL, {
            year: currentYear,
          }),
          body: t(AVERAGE_VALUE_DESCRIPTION_LABEL, {
            year: currentYear,
            indicator: t(
              'shared.projects.project.conservation.interactiveMap.infoPanel.labels.habitatIntactness',
            ).toLowerCase(),
          }),
        }}
      />
      <FactCapsule
        label={t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.fiveYearChange', {
          yearCount: currentYear - baselineYear,
        })}
        signed
        trend={<FactCapsuleTrend trend={changePercentage} />}
        fact={change}
        capsuleIconProps={{
          icon: <i className={trendIcon} />,
          variant: variant as 'positive' | 'negative' | 'default',
        }}
        infoPopoverProps={{
          title: t('shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.fiveYearChange.title'),
          body: t('shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.fiveYearChange.description', {
            indicator: t(
              'shared.projects.project.conservation.interactiveMap.infoPanel.labels.habitatIntactness',
            ).toLowerCase(),
          }),
        }}
      />
    </Stack>
  );
};

const FloraAndFauna = () => {
  const { t } = useTranslation();

  const currentYear = useFact('r1_conservation_current_year').value;
  const latestAverage = useCellFact('indicator_species_score');

  return (
    <Stack spacing={7}>
      <FactCapsule
        label={t(LAST_AVERAGE_VALUE_LABEL, {
          year: currentYear,
        })}
        fact={latestAverage}
        capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
        infoPopoverProps={{
          title: t(AVERAGE_VALUE_TITLE_LABEL, {
            year: currentYear,
          }),
          body: t(AVERAGE_VALUE_DESCRIPTION_LABEL, {
            year: currentYear,
            indicator: t(
              'shared.projects.project.conservation.interactiveMap.infoPanel.labels.floraAndFauna',
            ).toLowerCase(),
          }),
        }}
      />
    </Stack>
  );
};

const useCellFact = <T extends keyof CellProperties>(property: T) => {
  const controlsForm = useControlsContext();

  const cellProperties = controlsForm.watch('cell')?.properties;

  const value = cellProperties?.[property as T] as CellProperties[T] | undefined;

  return { name: property, value, unit: UnitEnum.none };
};
